<template>
  <div
    v-if="!!mediaUrl"
    :class="{
      'image--resized': shouldResize,
      'image--with-aspect': useAspect,
      'image--article-intro': isArticleIntro,
      'image--fill': alwaysFill,
      'image--inline': link,
    }"
    class="media image"
  >
    <div :style="paddingRatio" class="media__container">
      <WidgetLoadingIndicator :is-loaded="isLoaded" />
      <Component
        :is="link ? 'BlockLink' : 'div'"
        :link="link"
        :title="linkTitle"
      >
        <ClientOnly>
          <NuxtImg
            v-show="!!resizedImage"
            :alt="altText"
            :class="{ loaded: isLoaded }"
            :loading="loading"
            :src="resizedImage"
            :style="`object-fit: ${fit}; object-position: ${focusInPercent}`"
            @load="onLoadedImage"
          />
        </ClientOnly>
      </Component>
    </div>
    <BlockLink
      v-if="link"
      :label="linkTitle"
      :link="link"
      class="media__caption"
    >
      <template #default="slotProps">
        <span>
          {{ caption }}
        </span>
        <i class="icon">
          <Icon :name="slotProps.icon" />
        </i>
      </template>
    </BlockLink>
    <div
      v-else-if="showCaptions && caption && !isArticleIntro"
      class="media__caption"
    >
      {{ caption }}
    </div>
  </div>
  <div v-else class="media image">
    <div class="media__container">
      <WidgetLoadingIndicator />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    alwaysFill?: boolean
    isArticleIntro?: boolean
    showCaptions?: boolean
    useAspect?: boolean
    link?: string
    linkTitle?: string
    loading?: 'lazy' | 'eager'
    media: Media
    fit?: string
    maxHeight?: number
    maxWidth?: number
    quality?: number
  }>(),
  {
    alwaysFill: false,
    isArticleIntro: false,
    showCaptions: false,
    useAspect: false,
    link: '',
    linkTitle: '',
    loading: 'lazy',
    fit: 'cover',
    maxHeight: 0,
    maxWidth: 960,
    quality: 65,
  },
)
const {
  isLoaded,
  altText,
  mediaUrl,
  resizedImage,
  onLoadedImage,
  focusInPercent,
  dimensions,
  shouldResize,
} = useMedia(toRefs(props))

const caption = computed(() => props.linkTitle || props.media.title)
const paddingRatio = computed(() => {
  if (!mediaUrl.value || !props.useAspect) {
    return false
  }

  const aspect = (100 / dimensions.value.width) * dimensions.value.height
  return { 'padding-top': `${aspect.toFixed(6)}%` }
})
</script>

<style lang="less" scoped>
.media.image {
  .media__container {
    background: transparent;

    img {
      .block();

      width: auto;
      height: auto;
      margin: 0 auto;
      opacity: 0;
      transition: opacity 0.8s;

      &.loaded {
        opacity: 1;
      }
    }
  }

  .media__caption {
    .flex();
    .justify-between();
    .align-center();
    .font-xxs();
    .font-regular();
    .text-body();

    margin-left: @gutter;
    padding: @gutter @gutter @gutter-xs 0;
    border-bottom: @1px solid @color-primary;

    @media @tablet {
      margin: 0;
      padding: @gutter 0 @gutter-xs;
    }

    .icon {
      line-height: inherit;
    }
  }

  &.image--with-aspect {
    .media__container {
      .relative();

      img {
        .absolute();

        max-width: 100%;
        max-height: 100%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }

  &.image--resized {
    .media__container {
      .relative();

      img {
        .absolute();

        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        object-fit: cover;
        transform: none;
      }
    }
  }

  &.image--article-intro {
    .media__container {
      .image-aspect-3-2();

      @media @tablet {
        .static();
        .image-aspect-1-1();

        width: 150%; //makes image 3:2 while filling off screen
        transform: translate(-33.333%, 0);
      }

      img {
        width: 100%;
        height: 100%;
      }
    }
  }

  &.image--fill {
    .media__container {
      .relative();

      img {
        .absolute();

        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        transform: none;
      }
    }
  }

  &.image--inline {
    margin: @gutter-lg 0;
  }
}
@media print {
  .media.image {
    .media__container {
      img {
        opacity: 1;
      }
    }
  }
}
</style>
